// Tester translation preview (LOC-P06): the client of the private
// /api/l10n/preview routes and the controller that switches the one runtime
// of a client between the product release and the preview release.
//
// Preview is tab memory only: its mode and language are never written to the
// profile, to storage or to the catalog cache, and a reload starts on the
// product release again. Admission is the server's answer for the account on
// every request; nothing here grants or caches it beyond this page.
//
// Account binding is the preferences controller's (preferences.ts): every
// preview operation first mints the existing session access token, checks
// that the server minted it for the account this UI shows, and sends only
// that Bearer, never the cookie, and only to same-origin preview paths. The
// token lives for that one operation. A changed account or session drops the
// preview and returns the runtime to the product resolution of the new one.

import { PREVIEW_BASE_PATH } from "./loader";
import {
  createApiRequest,
  createPreferencesApi,
  parseBootstrap,
  PreferencesError,
  type AccountAccess,
  type LanguageBootstrap,
  type PreferencesApiOptions,
  type PreferencesController,
} from "./preferences";
import type { LocalizationRuntime } from "./runtime";
import type { FetchLike } from "./types";

export const PREVIEW_BOOTSTRAP_PATH = `${PREVIEW_BASE_PATH}/bootstrap`;

const PREVIEW_MANIFEST_URL = /^\/api\/l10n\/preview\/[a-z][a-z0-9-]+\/v[1-9][0-9]*\/manifest\.json$/;

export interface PreviewApi {
  /** The preferences mint: a session access token and the account it belongs to. */
  access(signal?: AbortSignal): Promise<AccountAccess>;
  /** The private bootstrap (preview-bootstrap.schema.json): 200 admitted, 403 not admitted, 401 signed out, 503 unavailable. */
  bootstrap(client: string, contract: number, access: AccountAccess, signal?: AbortSignal): Promise<LanguageBootstrap>;
  /** fetch for the runtime's loader: same-origin preview paths only, with the token of `access` and without the cookie. */
  catalogFetch(access: AccountAccess): FetchLike;
}

export interface PreviewApiOptions extends PreferencesApiOptions {
  /** Page origin the catalog requests must stay on; default location.origin. */
  readonly origin?: string;
}

export function createPreviewApi(options: PreviewApiOptions = {}): PreviewApi {
  const fetchImpl: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const request = createApiRequest(options);
  const { access } = createPreferencesApi(options);

  return {
    access,
    async bootstrap(client, contract, account, signal) {
      const query = `?client=${encodeURIComponent(client)}&contract=${encodeURIComponent(String(contract))}`;
      return parseBootstrap(await request(`${PREVIEW_BOOTSTRAP_PATH}${query}`, { method: "GET" }, account, signal), PREVIEW_MANIFEST_URL);
    },
    catalogFetch(account) {
      return (input, init = {}) => {
        const page = options.origin ?? globalThis.location?.origin;
        let url: URL;
        try {
          url = new URL(input);
          if (!page || url.origin !== new URL(page).origin || !url.pathname.startsWith(`${PREVIEW_BASE_PATH}/`)) throw new TypeError(url.pathname);
        } catch {
          return Promise.reject(new TypeError("not a same-origin preview catalog URL"));
        }
        const headers = { ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${account.token}` };
        return fetchImpl(url.href, { ...init, credentials: "omit", headers });
      };
    },
  };
}

// ---------------------------------------------------------------- controller

/**
 * unknown: not read for this account (no preview controls); checking: the
 * first read runs; admitted; denied: the server refused this account;
 * unavailable: admitted, but the preview release cannot be served now.
 */
export type PreviewAccess = "unknown" | "checking" | "admitted" | "denied" | "unavailable";

/** failed: the requested set did not load; unavailable: the server cannot serve the preview now; ended: access was refused or the session changed. */
export type PreviewErrorCode = "failed" | "unavailable" | "ended";

export interface PreviewState {
  /** The account of this state (the preferences controller's); preview never outlives it. */
  readonly accountId: string | null | undefined;
  readonly access: PreviewAccess;
  /** Languages of the preview release, drafts included, from the last admitted read. Never a profile choice. */
  readonly languages: readonly string[];
  /** Whether the live catalog is the preview release's. */
  readonly active: boolean;
  /** The preview language shown, or the requested one while entering or switching. */
  readonly language: string | null;
  readonly pending: "enter" | "switch" | "exit" | null;
  /** Why the latest request did not do what was asked; the live catalog is unchanged unless it ended. */
  readonly error: PreviewErrorCode | null;
}

export type PreviewResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: PreviewErrorCode | "not_admitted" | "superseded" };

export interface PreviewController {
  getSnapshot(): PreviewState;
  subscribe(listener: () => void): () => void;
  /** One private bootstrap read for the current account (admission and preview languages); concurrent calls share it. */
  discover(): Promise<void>;
  /** Shows `language` (default: the product choice) from the preview release; while active, switches its language. */
  enter(language?: string): Promise<PreviewResult>;
  /** Returns to the product release and the account's ordinary language. */
  exit(): Promise<void>;
}

export interface PreviewControllerOptions {
  readonly runtime: Pick<LocalizationRuntime, "identity" | "getSnapshot" | "setPreview">;
  /** The account source and identity flow: a stale session is handed to its reload(). */
  readonly preferences: Pick<PreferencesController, "getSnapshot" | "subscribe" | "reload">;
  readonly api?: PreviewApi;
}

const SUPERSEDED: PreviewResult = { ok: false, reason: "superseded" };
const codeOf = (e: unknown) => (e instanceof PreferencesError ? e.code : "network");

export function createPreviewController(options: PreviewControllerOptions): PreviewController {
  const { runtime, preferences } = options;
  const { identity } = runtime;
  const api = options.api ?? createPreviewApi();
  const off = { active: false, language: null, pending: null } as const;

  let state: PreviewState = { accountId: preferences.getSnapshot().accountId, access: "unknown", languages: [], ...off, error: null };
  const listeners = new Set<() => void>();
  const publish = (next: Partial<PreviewState>) => {
    state = { ...state, ...next };
    for (const listener of [...listeners]) listener();
  };

  // Bumped on every account change: no older answer is applied.
  let epoch = 0;
  // Bumped by every enter/exit: the last request wins.
  let seq = 0;
  let probe: { readonly epoch: number; readonly abort: AbortController; promise?: Promise<void> } | null = null;
  // The product choice the private bootstrap reported, the default preview language.
  let automatic: string | null = null;

  const previewLive = () => runtime.getSnapshot().channel === "preview";

  /** The session is no longer the account shown, or ended: the existing identity flow decides. */
  const identityStale = () => void preferences.reload();

  function reset(accountId: string | null | undefined): void {
    epoch += 1;
    seq += 1;
    probe?.abort.abort();
    probe = null;
    automatic = null;
    const live = state.active || state.pending !== null || previewLive();
    publish({ accountId, access: "unknown", languages: [], ...off, error: null });
    if (live) void runtime.setPreview(null);
  }

  preferences.subscribe(() => {
    const { accountId, status } = preferences.getSnapshot();
    if (accountId !== state.accountId) reset(accountId);
    else if (status === "account_changed" && (state.access !== "unknown" || state.active)) reset(accountId);
  });

  /** Ends preview because the server refused it or the session changed; the runtime returns to the product set. */
  async function end(access: PreviewAccess, error: PreviewErrorCode | null): Promise<void> {
    const live = state.active || previewLive();
    const id = ++seq;
    publish({ access, ...off, pending: live ? "exit" : null, error });
    if (!live) return;
    await runtime.setPreview(null);
    if (id === seq) publish({ pending: null });
  }

  /** A request that changed nothing: the runtime kept the live set (the product set on entry, the active preview on a switch). */
  function keep(error: PreviewErrorCode | null): void {
    const live = previewLive();
    publish({ active: live, language: live ? runtime.getSnapshot().language : null, pending: null, error });
  }

  function discover(): Promise<void> {
    const account = state.accountId;
    if (account == null || preferences.getSnapshot().status === "account_changed") return Promise.resolve();
    if (probe?.epoch === epoch && probe.promise) return probe.promise;
    const current = { epoch, abort: new AbortController() } as NonNullable<typeof probe>;
    probe = current;
    const stale = () => current.epoch !== epoch;
    if (state.access === "unknown" || state.access === "unavailable") publish({ access: "checking" });
    current.promise = (async () => {
      try {
        const access = await api.access(current.abort.signal);
        if (stale()) return;
        if (access.accountId !== account) {
          publish({ access: "unknown" });
          identityStale();
          return;
        }
        const boot = await api.bootstrap(identity.clientId, identity.contractVersion, access, current.abort.signal);
        if (stale()) return;
        const expected = `${PREVIEW_BASE_PATH}/${identity.clientId}/v${identity.contractVersion}/manifest.json`;
        if (boot.catalog.manifest_url !== expected || !boot.available_languages.includes(identity.baseLanguage)) {
          publish({ access: "unavailable" });
          return;
        }
        automatic = boot.ui_language;
        publish({ access: "admitted", languages: [...boot.available_languages] });
      } catch (e) {
        if (stale()) return;
        const code = codeOf(e);
        if (code === "forbidden") await end("denied", state.active ? "ended" : null);
        else if (code === "unauthenticated") {
          await end("unknown", state.active ? "ended" : null);
          identityStale();
        } else if (code === "unavailable" || code === "invalid_response") publish({ access: "unavailable" });
        // Offline or timed out: nothing is known about this account yet; an earlier answer stays.
        else if (state.access === "checking") publish({ access: "unknown" });
      } finally {
        if (probe === current) probe = null;
      }
    })();
    return current.promise;
  }

  function enter(requested?: string): Promise<PreviewResult> {
    const account = state.accountId;
    if (account == null || state.access !== "admitted") return Promise.resolve({ ok: false, reason: "not_admitted" });
    const language = requested ?? (state.active ? state.language : null) ?? automatic ?? identity.baseLanguage;
    if (!state.languages.includes(language)) return Promise.resolve({ ok: false, reason: "failed" });
    const forEpoch = epoch;
    const id = ++seq;
    const stale = () => forEpoch !== epoch || id !== seq;
    publish({ pending: state.active ? "switch" : "enter", language, error: null });
    return (async (): Promise<PreviewResult> => {
      let access: AccountAccess;
      try {
        access = await api.access();
      } catch (e) {
        if (stale()) return SUPERSEDED;
        if (codeOf(e) !== "unauthenticated") {
          keep("failed");
          return { ok: false, reason: "failed" };
        }
        await end("unknown", "ended");
        identityStale();
        return { ok: false, reason: "ended" };
      }
      if (stale()) return SUPERSEDED;
      if (access.accountId !== account) {
        await end("unknown", "ended");
        identityStale();
        return { ok: false, reason: "ended" };
      }
      const result = await runtime.setPreview({ language, fetch: api.catalogFetch(access) });
      if (stale()) return SUPERSEDED;
      if (result.ok) {
        keep(null);
        return { ok: true };
      }
      if (result.reason === "superseded") {
        keep(null);
        return SUPERSEDED;
      }
      if (result.status === 401 || result.status === 403) {
        await end(result.status === 403 ? "denied" : "unknown", "ended");
        if (result.status === 401) identityStale();
        return { ok: false, reason: "ended" };
      }
      const error: PreviewErrorCode = result.status === 503 ? "unavailable" : "failed";
      keep(error);
      return { ok: false, reason: error };
    })();
  }

  async function exit(): Promise<void> {
    if (!state.active && state.pending === null && !previewLive()) return;
    const id = ++seq;
    publish({ pending: "exit", error: null });
    // Whatever happens, the runtime leaves the preview: product set, cached set or embedded English.
    await runtime.setPreview(null);
    if (id === seq) publish({ ...off });
  }

  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    discover,
    enter,
    exit,
  };
}
